import React, { useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ShortTextIcon from "@mui/icons-material/ShortText";
import NotesIcon from "@mui/icons-material/Notes";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ScoreboardOutlinedIcon from "@mui/icons-material/ScoreboardOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ArrowDropDownCircleOutlinedIcon from "@mui/icons-material/ArrowDropDownCircleOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ThumbsUpDownOutlinedIcon from "@mui/icons-material/ThumbsUpDownOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

// The actual supported field-type registry (validators/formFieldValidator.js
// FIELD_TYPES) — every entry below must exist there. Grouped/labeled to
// match the Admin task spec's toolbox categories; icons are decorative only
// (field_type is what's ever persisted).
export const TOOLBOX_GROUPS = [
  {
    label: "Text",
    items: [
      { type: "SHORT_TEXT", label: "Short Text", icon: ShortTextIcon },
      { type: "LONG_TEXT", label: "Long Text", icon: NotesIcon },
      { type: "EMAIL", label: "Email", icon: EmailOutlinedIcon },
      { type: "MOBILE", label: "Mobile", icon: PhoneOutlinedIcon },
      { type: "URL", label: "URL", icon: LinkOutlinedIcon },
    ],
  },
  {
    label: "Number",
    items: [
      { type: "NUMBER", label: "Number", icon: TagOutlinedIcon },
      { type: "DECIMAL", label: "Decimal", icon: TagOutlinedIcon },
      { type: "PERCENTAGE", label: "Percentage", icon: PercentOutlinedIcon },
      { type: "SCORE", label: "Score", icon: ScoreboardOutlinedIcon },
      { type: "RATING", label: "Rating", icon: StarBorderIcon },
    ],
  },
  {
    label: "Date",
    items: [
      { type: "DATE", label: "Date", icon: EventOutlinedIcon },
      { type: "DATETIME", label: "Date & Time", icon: ScheduleOutlinedIcon },
    ],
  },
  {
    label: "Choice",
    items: [
      { type: "DROPDOWN", label: "Dropdown", icon: ArrowDropDownCircleOutlinedIcon },
      { type: "MULTI_SELECT", label: "Multi Select", icon: ChecklistOutlinedIcon },
      { type: "RADIO", label: "Radio", icon: RadioButtonCheckedOutlinedIcon },
      { type: "CHECKBOX", label: "Checkbox", icon: CheckBoxOutlinedIcon },
      { type: "YES_NO", label: "Yes / No", icon: ThumbsUpDownOutlinedIcon },
      { type: "RESULT_STATUS", label: "Result Status", icon: FlagOutlinedIcon },
    ],
  },
  {
    label: "Reference",
    items: [
      { type: "EMPLOYEE_SELECT", label: "Employee Select", icon: PersonSearchOutlinedIcon },
      { type: "PROJECT_SELECT", label: "Project Select", icon: FolderOutlinedIcon },
      { type: "PROJECT_MODULE_SELECT", label: "Project Module Select", icon: AccountTreeOutlinedIcon },
      { type: "TASK_SELECT", label: "Task Select", icon: TaskAltOutlinedIcon },
    ],
  },
  {
    label: "Media",
    items: [
      { type: "FILE_UPLOAD", label: "File Upload", icon: UploadFileOutlinedIcon },
      { type: "IMAGE_UPLOAD", label: "Image Upload", icon: ImageOutlinedIcon },
    ],
  },
];

// Drag payload MIME type for "new field from toolbox" drops — distinct
// from the "move an existing field" payload used by FormCanvas, so a drop
// handler can tell the two apart with dataTransfer.types.includes(...).
export const NEW_FIELD_DRAG_TYPE = "application/x-form-builder-new-field";

export default function FormFieldToolbox({ onAddField, disabled }) {
  const [search, setSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const handleDragStart = (fieldType) => (e) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(NEW_FIELD_DRAG_TYPE, fieldType);
    e.dataTransfer.setData("text/plain", fieldType);
  };

  const toggleGroup = (label) => setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  // Client-side only -- filters the same TOOLBOX_GROUPS array the
  // unfiltered toolbox always rendered from; onAddField/NEW_FIELD_DRAG_TYPE
  // contracts are untouched.
  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return TOOLBOX_GROUPS;
    return TOOLBOX_GROUPS
      .map((group) => ({ ...group, items: group.items.filter((item) => item.label.toLowerCase().includes(term)) }))
      .filter((group) => group.items.length > 0);
  }, [search]);

  return (
    <div className={`form-field-toolbox${disabled ? " is-readonly" : ""}`}>
      <div className="form-field-toolbox-heading">Field Toolbox</div>
      <div className="form-field-toolbox-hint">Drag a field onto a section, or click to add it to the selected section.</div>
      {disabled && (
        <div className="form-field-toolbox-readonly-notice">
          Field toolbox is disabled for published versions.
        </div>
      )}
      <div className="form-field-toolbox-search">
        <SearchIcon fontSize="small" aria-hidden="true" />
        <Form.Control
          size="sm"
          type="search"
          placeholder="Search fields..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search field types"
          disabled={disabled}
        />
      </div>
      {!visibleGroups.length && <div className="form-field-toolbox-empty">No field types match "{search}".</div>}
      {visibleGroups.map((group) => {
        const collapsed = !!collapsedGroups[group.label] && !search.trim();
        return (
          <div key={group.label} className="form-field-toolbox-group">
            <button
              type="button"
              className="form-field-toolbox-group-label"
              onClick={() => toggleGroup(group.label)}
              aria-expanded={!collapsed}
            >
              {collapsed ? <ExpandMoreIcon fontSize="inherit" /> : <ExpandLessIcon fontSize="inherit" />}
              {group.label}
            </button>
            {!collapsed && (
              <div className="form-field-toolbox-cards">
                {group.items.map(({ type, label, icon: Icon }) => (
                  <div
                    key={type}
                    className={`form-field-toolbox-card${disabled ? " is-disabled" : ""}`}
                    draggable={!disabled}
                    onDragStart={disabled ? undefined : handleDragStart(type)}
                    onClick={disabled ? undefined : () => onAddField(type)}
                    title={disabled ? "Field toolbox is disabled for published versions." : `Drag onto a section, or click to add "${label}" to the selected section.`}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-label={`Add ${label} field`}
                    aria-disabled={disabled}
                  >
                    <span className="form-field-toolbox-drag-dots" aria-hidden="true">⋮⋮</span>
                    <Icon fontSize="small" className="form-field-toolbox-icon" aria-hidden="true" />
                    <span className="form-field-toolbox-label">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
